/**
 * What the site says about itself, read back to the operator.
 *
 * The first three lines are the ones that win a call. An operator who can open
 * with "you sell used vans to families and your front page never says so" is
 * having a different conversation from one who opens with "I noticed some
 * issues with your website". Those three lines are the model's reading of the
 * owner's own pages, not our guess about their business.
 *
 * THE OFFERING LINE IS ALSO A WARNING LIGHT. If the model could not tell what
 * the company does after reading up to ten of their pages, no visitor can
 * either - and that is the single most expensive defect this tool can find. It
 * is left as plain text rather than dressed up, because the sentence itself is
 * the evidence.
 *
 * The observations below repeat findings that are already in the findings tab.
 * That repetition is deliberate: this tab is the one an operator reads while
 * preparing, grouped by the page it is about, and sending them to another tab
 * to see which page a defect sits on would break exactly that reading.
 */
import { MessageSquare, Target, Users } from "lucide-react";
import type { BrainContent, BrainSeverity } from "../../../../lib/siteBrain";
import { SEVERITY_COLOUR } from "../../site-scanner/severity";
import type { T } from "../../site-scanner/types";

export function BrainContentPanel({ content, t }: { content: BrainContent | null; t: T }) {
  if (!content) return <p className="text-sm text-muted-foreground">{t("brain.content.missing")}</p>;

  return (
    <div className="space-y-3">
      <dl className="space-y-2">
        <Line icon={Target} label={t("brain.content.offering")} value={content.offering} />
        <Line icon={Users} label={t("brain.content.audience")} value={content.audience} />
        <Line
          icon={MessageSquare}
          label={t("brain.content.cta")}
          value={content.primaryCta}
          // A site that asks the visitor to do nothing at all is a finding in
          // its own right, so the empty case gets a sentence rather than a dash.
          empty={t("brain.content.noCta")}
        />
      </dl>

      {content.observations.length ? (
        <ul className="space-y-1.5">
          {content.observations.map((o, i) => (
            <Observation key={`${o.kind}:${o.page}:${i}`} kind={o.kind} severity={o.severity} page={o.page} detail={o.detail} t={t} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{t("brain.content.clean")}</p>
      )}

      <p className="text-xs text-muted-foreground">{t("brain.content.model", { model: content.model })}</p>
    </div>
  );
}

function Line({
  icon: Icon,
  label,
  value,
  empty,
}: {
  icon: typeof Target;
  label: string;
  value: string | null;
  empty?: string;
}) {
  return (
    <div className="flex gap-2.5 rounded-2xl bg-[var(--crm-sunk)] px-3.5 py-2.5">
      <Icon className="mt-0.5 size-4 shrink-0 text-primary" />
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="text-sm leading-snug">{value || empty}</dd>
      </div>
    </div>
  );
}

function Observation({
  kind,
  severity,
  page,
  detail,
  t,
}: {
  kind: string;
  severity: BrainSeverity;
  page: string;
  detail: string;
  t: T;
}) {
  const colour = SEVERITY_COLOUR[severity];
  return (
    <li
      className="rounded-2xl bg-[var(--crm-sunk)] px-3.5 py-2.5"
      style={{ boxShadow: `inset 3px 0 0 0 color-mix(in oklab, ${colour} 65%, transparent)` }}
    >
      <p className="text-sm leading-snug">{detail}</p>
      <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <span>{t(`brain.check.${kind}`)}</span>
        <span aria-hidden>-</span>
        <a
          href={page}
          target="_blank"
          rel="noreferrer noopener"
          className="truncate underline-offset-2 hover:text-foreground hover:underline"
        >
          {page}
        </a>
      </p>
    </li>
  );
}
